// Reporting zod DTOs (S7) — BUILD_SPEC.md §13 (all reports) + §9.6 (mass balance) +
// §9.7 (time-windowed genealogy). Owned by G19 (plan.md) — this is the ONLY core file
// this goal fills; the barrel (schemas/index.ts) already re-exports it (S0).
//
// Reporting is READ-ONLY over every other module's tables — there is deliberately no
// schema/reporting.ts table file (no new tables) and no rls/reporting.sql (nothing to
// protect that isn't already RLS-scoped on the owning table). Every query schema
// below is a GET query-string shape (`z.coerce.*` throughout, since HTTP query params
// always arrive as strings — same convention `modules/purchasing`'s
// `listQueryBaseSchema` and `modules/stores/shared.ts`'s `listQuerySchema` use).
//
// CSV toggle: every report accepts `?format=csv` (goal text: "each ... CSV-exportable
// (an `?format=csv` ... variant)"). Modelled once here as `reportFormatSchema` and
// spliced into every report's query schema via `.extend`.
import { z } from 'zod';
import { ulidSchema } from './common.js';
import { BASE_UOMS, LOCATION_TYPES, MOVEMENT_SOURCES, MOVEMENT_TYPES, PRODUCT_TYPES, VAT_TREATMENTS } from '../enums.js';
export const reportFormatSchema = z.enum(['json', 'csv']).optional().default('json');
/** Shared site + date-range filter fragment — the great majority of §13 reports are
 * scoped this way. `from`/`to` are inclusive on `occurredAt`/the relevant business
 * date; both optional (an unbounded report is still a valid report — e.g. "everything
 * to date"). */
const siteAndRangeSchema = z.object({
    siteId: ulidSchema.optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
});
/** Non-cursor result cap shared by the reports that don't need full cursor pagination
 * (see modules/reporting/shared.ts's header for why: these are analytical/summary
 * queries, not high-volume document registers — a single generous page is the
 * pragmatic v1 shape). The movement ledger browser (genuinely high-volume) is the one
 * report with real cursor pagination — see `movementLedgerQuerySchema` below. */
const reportLimitSchema = z.coerce.number().int().positive().max(2000).default(500);
// ---------------------------------------------------------------------------
// 1. Stock on hand & valuation
// ---------------------------------------------------------------------------
export const stockOnHandQuerySchema = z.object({
    siteId: ulidSchema.optional(),
    locationId: ulidSchema.optional(),
    productId: ulidSchema.optional(),
    format: reportFormatSchema,
});
/** One row of `GET /reporting/stock-on-hand` (declarative response DTO — same
 * documentation-only role as `movementLedgerRowSchema`/`productSummaryRowSchema`
 * below: not wired into stock.ts as runtime response validation). `baseUom` is
 * an additive field (2026-08-26, Stuart's R/tonne-vs-c/kg display ruling) — the
 * web needs it to know whether a KG product's `avgCostCentsPerUnit` should
 * render as Rand-per-tonne or Rand-per-unit. */
export const stockOnHandRowSchema = z.object({
    siteId: ulidSchema,
    locationId: ulidSchema,
    locationCode: z.string(),
    locationName: z.string(),
    productId: ulidSchema,
    productSku: z.string(),
    productName: z.string(),
    baseUom: z.enum(BASE_UOMS),
    qty: z.string(),
    wetKg: z.string(),
    dryKg: z.string(),
    handlingUnits: z.number().int(),
    avgCostCentsPerUnit: z.string(),
    valueCents: z.string(),
});
// ---------------------------------------------------------------------------
// 2. Movement ledger browser
// ---------------------------------------------------------------------------
export const movementLedgerQuerySchema = z.object({
    siteId: ulidSchema.optional(),
    locationId: ulidSchema.optional(),
    productId: ulidSchema.optional(),
    type: z.enum(MOVEMENT_TYPES).optional(),
    referenceType: z.string().optional(),
    referenceId: z.string().optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(500).default(100),
    // Products-module running-balance addon (Unleashed parity — not part of the
    // original BUILD_SPEC §13 numbering). Query-string boolean-ish idiom matches
    // every `isActive` param in this codebase (config.ts/purchasing.ts/sales.ts):
    // an explicit 'true'/'false' string, absent -> false. Only meaningful (and only
    // accepted by the handler) when `productId` is also given — see movements.ts.
    withRunningBalance: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => v === 'true'),
    format: reportFormatSchema,
});
/** One row of `GET /reporting/movements` (declarative response DTO — mirrors
 * `productSummaryRowSchema`'s role below: documents the wire shape for consumers,
 * not wired into movements.ts as runtime response validation, matching every other
 * report row in this file). `runningQty`/`preAvgCostCentsPerUnit`/
 * `postAvgCostCentsPerUnit` are present ONLY when `withRunningBalance=true` — the
 * latter two are the product-detail data-layer sibling (Amendment 2026-08-26):
 * pool weighted-average cost per base unit immediately before/after this movement,
 * folded in the SAME (occurredAt, id) full-history scan `runningQty` already uses,
 * always bucketed per the movement's own `siteId` (stock_pools is per-site, §9.1) —
 * see movements.ts's header for the deliberate asymmetry with `runningQty` itself,
 * which folds across every site when no `siteId` filter is given. */
export const movementLedgerRowSchema = z.object({
    id: ulidSchema,
    siteId: ulidSchema,
    productId: ulidSchema,
    productSku: z.string(),
    productName: z.string(),
    locationId: ulidSchema,
    locationCode: z.string(),
    type: z.enum(MOVEMENT_TYPES),
    qty: z.string(),
    mcPct: z.string().nullable(),
    dryKg: z.string(),
    handlingUnits: z.number().int().nullable(),
    unitCostCents: z.string().nullable(),
    valueCents: z.string(),
    referenceType: z.string(),
    referenceId: z.string(),
    reason: z.string().nullable(),
    source: z.enum(MOVEMENT_SOURCES),
    occurredAt: z.string(),
    reversalOf: ulidSchema.nullable(),
    createdBy: z.string().nullable(),
    createdByName: z.string().nullable(),
    runningQty: z.string().optional(),
    preAvgCostCentsPerUnit: z.string().nullable().optional(),
    postAvgCostCentsPerUnit: z.string().nullable().optional(),
});
// ---------------------------------------------------------------------------
// 3. Mass balance (§9.6) — THE key correctness report
// ---------------------------------------------------------------------------
export const massBalanceQuerySchema = z.object({
    siteId: ulidSchema,
    from: z.coerce.date(),
    to: z.coerce.date(),
    format: reportFormatSchema,
});
// ---------------------------------------------------------------------------
// 4. Yield by stage/period + per-run vs recipe (§9.5)
// ---------------------------------------------------------------------------
export const yieldReportQuerySchema = z.object({
    siteId: ulidSchema.optional(),
    stageId: ulidSchema.optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    format: reportFormatSchema,
});
// ---------------------------------------------------------------------------
// 5. Silo drift history
// ---------------------------------------------------------------------------
export const siloDriftQuerySchema = z.object({
    siteId: ulidSchema.optional(),
    locationId: ulidSchema.optional(),
    productId: ulidSchema.optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    limit: reportLimitSchema,
    format: reportFormatSchema,
});
// ---------------------------------------------------------------------------
// 6. Open/partial PO book + supplier spend
// ---------------------------------------------------------------------------
export const purchasingReportQuerySchema = z.object({
    siteId: ulidSchema.optional(),
    supplierId: ulidSchema.optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    format: reportFormatSchema,
});
// ---------------------------------------------------------------------------
// 7. Sales by customer/product/period
// ---------------------------------------------------------------------------
export const salesReportQuerySchema = siteAndRangeSchema.extend({
    customerId: ulidSchema.optional(),
    productId: ulidSchema.optional(),
    format: reportFormatSchema,
});
// ---------------------------------------------------------------------------
// 8. Uninvoiced dispatches
// ---------------------------------------------------------------------------
export const uninvoicedDispatchesQuerySchema = z.object({
    siteId: ulidSchema.optional(),
    customerId: ulidSchema.optional(),
    format: reportFormatSchema,
});
// ---------------------------------------------------------------------------
// 9. Consumable spend by cost centre
// ---------------------------------------------------------------------------
export const consumableSpendQuerySchema = siteAndRangeSchema.extend({
    costCentreId: ulidSchema.optional(),
    format: reportFormatSchema,
});
// ---------------------------------------------------------------------------
// 10. Reorder suggestions
// ---------------------------------------------------------------------------
export const reorderSuggestionsQuerySchema = z.object({
    siteId: ulidSchema.optional(),
    format: reportFormatSchema,
});
// ---------------------------------------------------------------------------
// 11. Margin per dispatch/invoice
// ---------------------------------------------------------------------------
export const marginReportQuerySchema = siteAndRangeSchema.extend({
    customerId: ulidSchema.optional(),
    productId: ulidSchema.optional(),
    groupBy: z.enum(['dispatch', 'invoice']).optional().default('dispatch'),
    format: reportFormatSchema,
});
// ---------------------------------------------------------------------------
// 12. Time-windowed genealogy (§9.7)
// ---------------------------------------------------------------------------
export const genealogyQuerySchema = z.object({
    dispatchId: ulidSchema,
    format: reportFormatSchema,
});
// ---------------------------------------------------------------------------
// Home-screen site status strip (BUILD_SPEC.md §11) — the four dashboard tiles the
// web's `useSiteStatus()` hook + `SiteStatusStrip` consume. FIELD NAMES ARE PINNED BY
// THE WEB (apps/web/src/hooks/useSiteStatus.ts's `SiteStatus`/`SiloStatus` interfaces)
// — the web is the consumer, so this DTO matches its names verbatim: `openPurchase
// Orders`, `uninvoicedDispatches`, `lowStockCount`, and `silos` (an ARRAY, since the
// strip renders "Silos tracked" from `data.silos.length`, not a scalar count).
// ---------------------------------------------------------------------------
export const siteStatusQuerySchema = z.object({
    siteId: ulidSchema.optional(),
});
export const siloStatusSchema = z.object({
    siloId: ulidSchema,
    name: z.string(),
    /** Latest estimated fill %, or null when the silo has no PCT-unit reading yet. */
    estimatedPct: z.number().nullable(),
});
export const siteStatusResponseSchema = z.object({
    openPurchaseOrders: z.number().int(),
    uninvoicedDispatches: z.number().int(),
    lowStockCount: z.number().int(),
    silos: z.array(siloStatusSchema),
});
// ---------------------------------------------------------------------------
// Products module — product summary (Unleashed-parity "Products" list: one row
// per product with stock/cost aggregates). Not part of the original BUILD_SPEC
// §13 numbering; added for the Products module backend.
//
// `isActive` deliberately has a THREE-way shape unlike every other module's
// `isActiveQueryParamSchema` (config.ts/purchasing.ts/sales.ts, which is
// true/false/absent-means-both): here absent means "active only" (Unleashed's
// own default — obsolete products are hidden unless asked for), and `all` is the
// explicit escape hatch to see everything. This is a deliberate, spec-pinned
// deviation from the codebase's usual isActive idiom — flagged here so a future
// reader doesn't "fix" it to match the other modules.
// ---------------------------------------------------------------------------
export const productSummaryQuerySchema = z.object({
    siteId: ulidSchema.optional(),
    locationId: ulidSchema.optional(),
    q: z.string().min(1).max(200).optional(),
    type: z.enum(PRODUCT_TYPES).optional(),
    isActive: z.enum(['true', 'false', 'all']).optional().default('true'),
    format: reportFormatSchema,
});
export const productSummaryRowSchema = z.object({
    productId: ulidSchema,
    sku: z.string(),
    name: z.string(),
    type: z.enum(PRODUCT_TYPES),
    baseUom: z.string(),
    isActive: z.boolean(),
    qty: z.string(),
    valueCents: z.string(),
    avgCostCentsPerUnit: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Products module — product detail (Unleashed "Stock Enquiry" parity). Not part
// of the original BUILD_SPEC §13 numbering; the per-product drill-down sibling of
// `product-summary` above — `GET /reporting/product-detail?productId=` (Amendment
// 2026-08-26, Stuart's ruling: "product pages at least as rich as Unleashed").
// See docs/BUILD_SPEC.md's 2026-08-26 amendment for the allocated/last-cost/
// per-location-value definitions this DTO's fields encode.
// ---------------------------------------------------------------------------
export const productDetailQuerySchema = z.object({
    productId: ulidSchema,
});
export const productDetailLocationSchema = z.object({
    locationId: ulidSchema,
    code: z.string(),
    name: z.string(),
    type: z.enum(LOCATION_TYPES),
    qty: z.string(),
    valueCents: z.string(),
    allocatedQty: z.string(),
    onPurchaseQty: z.string(),
});
export const productDetailSiteSchema = z.object({
    siteId: ulidSchema,
    siteCode: z.string(),
    siteName: z.string(),
    qty: z.string(),
    valueCents: z.string(),
    avgCostCentsPerUnit: z.string().nullable(),
    locations: z.array(productDetailLocationSchema),
});
export const productDetailResponseSchema = z.object({
    product: z.object({
        id: ulidSchema,
        sku: z.string(),
        name: z.string(),
        type: z.enum(PRODUCT_TYPES),
        baseUom: z.enum(BASE_UOMS),
        isActive: z.boolean(),
        isMoistureTracked: z.boolean(),
        defaultMcPct: z.string().nullable(),
        vatTreatment: z.enum(VAT_TREATMENTS),
        minLevel: z.string().nullable(),
        reorderQty: z.string().nullable(),
    }),
    totals: z.object({
        qty: z.string(),
        valueCents: z.string(),
        avgCostCentsPerUnit: z.string().nullable(),
        allocatedQty: z.string(),
        availableQty: z.string(),
        onPurchaseQty: z.string(),
        lastCostCentsPerUnit: z.string().nullable(),
        lastCostAt: z.string().nullable(),
    }),
    sites: z.array(productDetailSiteSchema),
});
//# sourceMappingURL=reporting.js.map